import { Component, DestroyRef, inject, Input, Output, EventEmitter, computed, effect, signal, untracked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatBadgeModule } from '@angular/material/badge';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  UserStateService,
  AppStateService,
  CartService,
  GlobalsService,
  UploadService,
  UploadResilienceService,
  AlertService,
  PermissionsService,
  PriceService,
  BugReportService,
  UserAvatarService
} from '../../../core/services';
import { AppState } from '../../../core/models';
import { openBugReportDialog } from '../bug-report-dialog/open-bug-report-dialog';
import { WhatsNewDialogComponent } from '../whats-new-dialog/whats-new-dialog.component';
import { WHATS_NEW_LATEST_ID, WHATS_NEW_SEEN_STORAGE_KEY } from '../whats-new-dialog/whats-new-items';
import { SupportIconComponent } from '../support-icon/support-icon.component';
import { OrderGroupingDialogComponent } from '../../../features/orders/order-grouping-dialog/order-grouping-dialog.component';
import { ChangePasswordDialogComponent } from '../change-password-dialog/change-password-dialog.component';

/** Badge poll cadence, and the smallest gap between two refreshes. */
const BUG_UNREAD_POLL_MS = 5 * 60 * 1000;
const BUG_UNREAD_MIN_GAP_MS = 60 * 1000;

@Component({
  selector: 'app-topbar',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatToolbarModule,
    MatButtonModule,
    MatIconModule,
    MatBadgeModule,
    MatMenuModule,
    MatProgressBarModule,
    MatTooltipModule,
    MatDialogModule,
    TranslateModule,
    SupportIconComponent
  ],
  templateUrl: './topbar.component.html',
  styleUrls: ['./topbar.component.scss']
})
export class TopbarComponent {
  private readonly userState = inject(UserStateService);
  readonly appState = inject(AppStateService);
  private readonly cartService = inject(CartService);
  readonly globals = inject(GlobalsService);
  private readonly uploadService = inject(UploadService);
  readonly uploadResilience = inject(UploadResilienceService);
  private readonly dialog = inject(MatDialog);
  private readonly alert = inject(AlertService);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);
  readonly perms = inject(PermissionsService);
  private readonly priceService = inject(PriceService);
  private readonly bugReportService = inject(BugReportService);
  private readonly userAvatar = inject(UserAvatarService);
  private readonly destroyRef = inject(DestroyRef);

  /**
   * Unread support replies on the user's OWN bug reports (customer side of
   * the thread) — the badge on the bug icon and on the user-menu entry.
   * Refetched whenever the acting user changes (login, support session
   * swap) and every 5 minutes while logged in; the customer list / thread
   * screens refresh it on their own after mark-read.
   */
  readonly bugUnreadCount = computed(() => this.bugReportService.myUnreadCount() || 0);
  private bugUnreadTimer: ReturnType<typeof setInterval> | null = null;
  /**
   * Track the ID, not the user object: `setUser(...)` runs on every address
   * edit / profile save / avatar change, and reading `user()` directly would
   * restart the interval (so the 5-minute refresh never fires for an active
   * user) and fire a redundant request each time. A computed over the id only
   * notifies when the acting user actually changes.
   */
  private readonly bugUnreadUserId = computed(() => this.userState.user()?.id ?? null);
  private readonly bugUnreadSync = effect(() => {
    const userId = this.bugUnreadUserId();
    untracked(() => {
      this.stopBugUnreadTimer();
      if (!userId) {
        this.bugReportService.clearMyUnreadCount();
        return;
      }
      // force: a user swap (login, entering/leaving a support session) must
      // fetch the new account's count even if we just refreshed the old one's.
      this.refreshBugUnread(true);
      this.bugUnreadTimer = setInterval(() => this.refreshBugUnread(true), BUG_UNREAD_POLL_MS);
    });
  });

  /** Refresh the badge, at most once per BUG_UNREAD_MIN_GAP_MS. */
  private lastBugUnreadRefresh = 0;
  private refreshBugUnread(force = false): void {
    const now = Date.now();
    if (!force && now - this.lastBugUnreadRefresh < BUG_UNREAD_MIN_GAP_MS) return;
    this.lastBugUnreadRefresh = now;
    this.bugReportService.refreshMyUnreadCount();
  }

  private stopBugUnreadTimer(): void {
    if (this.bugUnreadTimer) {
      clearInterval(this.bugUnreadTimer);
      this.bugUnreadTimer = null;
    }
  }

  // Coming back to the tab is the moment a support reply is most likely to
  // have landed — refresh the badge then, on top of the poll. Throttled:
  // dismissing a file picker, a print dialog or an OS alert also raises
  // `focus`, and alt-tabbing shouldn't turn into a burst of requests.
  private readonly onWindowFocus = (): void => {
    if (this.bugUnreadUserId()) this.refreshBugUnread();
  };

  constructor() {
    window.addEventListener('focus', this.onWindowFocus);
    this.destroyRef.onDestroy(() => {
      this.stopBugUnreadTimer();
      window.removeEventListener('focus', this.onWindowFocus);
    });
  }

  /** Display-only price inflation ("Inflacionar Preços"). Lives in
   *  PriceService (applied inside the single price-formatting funnel);
   *  the topbar only exposes the slider + the active chip. */
  readonly priceInflation = this.priceService.inflationFactor;
  readonly inflationMin = PriceService.INFLATION_MIN;
  readonly inflationMax = PriceService.INFLATION_MAX;

  inflationLabel(): string {
    return `${this.priceInflation().toFixed(1)}x`;
  }

  onInflationInput(event: Event): void {
    this.priceService.setInflationFactor(Number((event.target as HTMLInputElement).value));
  }

  resetInflation(): void {
    this.priceService.setInflationFactor(PriceService.INFLATION_MIN);
  }

  @Input() showShadow = true;
  @Input() lastHome: AppState = AppState.HOME_DREAMBOOKSPRO;

  @Output() navigateHome = new EventEmitter<AppState>();
  @Output() navigateOrders = new EventEmitter<void>();
  @Output() navigateDrafts = new EventEmitter<void>();
  @Output() navigateCart = new EventEmitter<void>();
  @Output() navigateProfile = new EventEmitter<void>();
  @Output() navigateProjects = new EventEmitter<void>();
  @Output() navigateUploads = new EventEmitter<void>();
  @Output() navigateCollections = new EventEmitter<void>();
  @Output() navigateDesigner = new EventEmitter<void>();
  @Output() navigateSupport = new EventEmitter<void>();
  @Output() navigateGalleries = new EventEmitter<void>();
  @Output() navigateAlbumDesign = new EventEmitter<void>();
  @Output() logout = new EventEmitter<void>();

  readonly user = this.userState.user;
  readonly isLoggedIn = this.userState.isLoggedIn;
  /**
   * Identity comes from UserAvatarService so every surface that draws "me"
   * (here, the bug-report thread, …) shows the same picture and initials.
   * Sub-user sessions resolve to the team member, not the parent account.
   */
  readonly fullName = this.userAvatar.displayName;
  readonly cartItemsCount = this.cartService.totalItems;
  readonly partner = this.appState.partner;
  readonly isUploading = this.uploadService.isUploading;
  readonly uploadProgress = this.uploadService.totalProgress;

  // Use observables for reactive permissions
  readonly isSupport$ = this.globals.isSupport$;
  readonly isDesigner$ = this.globals.isDesigner$;
  readonly isAdmin$ = this.globals.isAdmin$;

  // Also keep getters for imperative code
  get allowOrdersGrouping(): boolean { return this.user()?.allowOrdersGrouping === true; }

  get isSupport(): boolean { return this.globals.isSupport; }
  get isDesigner(): boolean { return this.globals.isDesigner; }
  get isAdmin(): boolean { return this.globals.isAdmin; }
  get hasAlbumDesign(): boolean { return this.user()?.hasAlbumDesign === true; }

  readonly hasDreambooksPro = this.globals.hasDreambooksPro;
  readonly hasOneLabPro = this.globals.hasOneLabPro;
  readonly hasLfmPro = this.globals.hasLfmPro;

  // Identity (initials, avatar URL, load-failure fallback) is shared with
  // every other surface that draws the signed-in user — see UserAvatarService.
  readonly initials = this.userAvatar.initials;
  readonly avatarUrl = this.userAvatar.avatarUrl;
  readonly showAvatarImage = this.userAvatar.showImage;

  onAvatarError(): void {
    this.userAvatar.markFailed();
  }

  readonly AppState = AppState;

  getLogoClass(): string {
    switch (this.lastHome) {
      case AppState.HOME_DREAMBOOKSPRO:
        return 'dreambooks-logo';
      case AppState.HOME_ONELABPRO:
        return 'onelab-logo';
      case AppState.HOME_LFMPRO:
        return 'lfm-logo';
      default:
        return 'dreambooks-logo';
    }
  }

  onLogoClick(): void {
    this.navigateHome.emit(this.lastHome);
  }

  onProductsClick(): void {
    this.navigateHome.emit(this.lastHome);
  }

  onOrdersClick(): void {
    this.navigateOrders.emit();
  }

  onDraftsClick(): void {
    this.navigateDrafts.emit();
  }

  onCartClick(): void {
    this.navigateCart.emit();
  }

  onGalleriesClick(): void {
    this.navigateGalleries.emit();
  }

  onAlbumDesignClick(): void {
    this.navigateAlbumDesign.emit();
  }

  onProfileClick(): void {
    this.navigateProfile.emit();
  }

  onProjectsClick(): void {
    this.navigateProjects.emit();
  }

  onUploadsClick(): void {
    this.navigateUploads.emit();
  }

  onCollectionsClick(): void {
    this.navigateCollections.emit();
  }

  onDesignerClick(): void {
    this.navigateDesigner.emit();
  }

  onSupportClick(): void {
    this.navigateSupport.emit();
  }

  openBugReport(): void {
    openBugReportDialog(this.dialog, this.alert, this.translate).subscribe();
  }

  /** "Novidades" pill: dot until the CURRENT latest entry has been seen. */
  readonly hasUnseenWhatsNew = signal(this.readWhatsNewSeenId() !== WHATS_NEW_LATEST_ID);

  private readWhatsNewSeenId(): string | null {
    try { return localStorage.getItem(WHATS_NEW_SEEN_STORAGE_KEY); } catch { return null; }
  }

  openWhatsNew(): void {
    this.hasUnseenWhatsNew.set(false);
    try { localStorage.setItem(WHATS_NEW_SEEN_STORAGE_KEY, WHATS_NEW_LATEST_ID); } catch { /* storage blocked */ }
    // No width/height here: the component sizes (and resizes) itself.
    this.dialog.open(WhatsNewDialogComponent, {
      maxWidth: '95vw',
      panelClass: 'confirm-dialog-panel',
      autoFocus: false
    });
  }

  openBugList(): void {
    const locale = this.appState.locale() || 'pt_PT';
    this.router.navigate([locale, 'support', 'bug-reports']);
  }

  /** Customer area: the user's own reports + support thread. */
  openMyBugReports(): void {
    const locale = this.appState.locale() || 'pt_PT';
    this.router.navigate([locale, 'bug-reports']);
  }

  openOrderGrouping(): void {
    this.dialog.open(OrderGroupingDialogComponent, {
      width: '640px',
      maxWidth: '95vw',
      panelClass: 'order-grouping-dialog-panel',
      autoFocus: false
    });
  }

  onInvoicesClick(): void {
    this.router.navigate(this.appState.buildLocaleCommands(['invoices']));
  }

  openChangePassword(): void {
    this.dialog.open(ChangePasswordDialogComponent, {
      width: '440px',
      maxWidth: '95vw',
      panelClass: 'confirm-dialog-panel',
      autoFocus: 'first-tabbable'
    });
  }

  onLogoutClick(): void {
    this.logout.emit();
  }

  onToggleHidePrices(): void {
    this.appState.toggleHidePrices();
  }
}
